<script setup lang="ts">
import { computed } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import BuildStats from "@/components/characters/BuildStats.vue";
import { trackEvent } from "@/appdata/analytics";

const props = defineProps<{
    steps: { label: string }[];
    submitLabel?: string;
}>();

const emit = defineEmits(['submit']);

const ux = useUxData();
const setupStore = useGuidedSetupStore();

const progressPercent = computed(() => setupStore.progressPercent);
const currentStep = computed(() => setupStore.currentStep);
const isFirstStep = computed(() => setupStore.isFirstStep);
const isLastStep = computed(() => setupStore.isLastStep);
const canProceed = computed(() => setupStore.canProceed);
const canSubmit = computed(() => setupStore.canSubmit);
const isSubmitting = computed(() => setupStore.isSubmitting);

const canGoToStep = (index: number): boolean => {
    if (index <= currentStep.value) return true;
    for (let i = 0; i < index; i++) {
        if (!setupStore.stepValidation[i]) return false;
    }
    return true;
};

const onPrevClick = () => {
    setupStore.prevStep();
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const onNextClick = () => {
    trackEvent(`${setupStore.flowTitle}: ${props.steps[currentStep.value]?.label ?? ''}`);
    setupStore.nextStep();
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const onStepClick = (index: number) => {
    if (canGoToStep(index)) {
        setupStore.goToStep(index);
    }
};

const onSubmitClick = () => {
    emit('submit');
};
</script>

<template>
    <div class="guided-stepper">
        <div class="stepper-header mb-4">
            <div class="progress mb-3" style="height: 8px;">
                <div
                    class="progress-bar bg-success"
                    role="progressbar"
                    :style="{ width: progressPercent + '%' }"
                    :aria-valuenow="progressPercent"
                    aria-valuemin="0"
                    aria-valuemax="100"
                ></div>
            </div>
            <div class="step-indicators d-flex justify-content-between">
                <button
                    v-for="(step, index) in steps"
                    :key="index"
                    type="button"
                    class="step-indicator btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                    style="width: 32px; height: 32px; padding: 0;"
                    :class="{
                        'btn-success': index < currentStep,
                        'btn-primary': index === currentStep,
                        'btn-outline-secondary': index > currentStep
                    }"
                    :disabled="!canGoToStep(index)"
                    :title="step.label"
                    @click="onStepClick(index)"
                >
                    <span v-if="index < currentStep" class="fa-light fa-check"></span>
                    <span v-else>{{ index + 1 }}</span>
                </button>
            </div>
            <p class="step-label text-center mt-2 mb-0 text-muted small">
                {{ steps[currentStep]?.label }}
            </p>
        </div>

      <div class="stepper-content py-3">
            <slot :name="'step-' + currentStep"></slot>
        </div>

      <Teleport to="#guided-setup-modal-footer">
        <div class="stepper-footer d-flex justify-content-between flex-fill">
          <BuildStats v-if="setupStore.activeFlow === 'character'"/>

          <button
              v-if="!isFirstStep"
              type="button"
              class="btn btn-link text-muted me-auto order-lg-first"
              @click="onPrevClick"
          >
            <span class="fa-light fa-chevron-left me-2"></span>
            {{ ux.labels?.guidedSetup?.actions?.back ?? 'Back' }}
          </button>
          <span v-else class="me-auto"></span>

          <button
              v-if="!isLastStep"
              type="button"
              class="btn btn-primary ms-auto"
              :disabled="!canProceed"
              @click="onNextClick"
          >
            {{ ux.labels?.guidedSetup?.actions?.next ?? 'Next' }}
            <span class="fa-light fa-chevron-right ms-2"></span>
          </button>
          <button
              v-else
              type="button"
              class="btn btn-success ms-auto"
              :disabled="!canSubmit || isSubmitting"
              @click="onSubmitClick"
          >
            <span v-if="isSubmitting" class="fa-light fa-spinner fa-spin me-2"></span>
            {{ submitLabel ?? ux.labels?.actions?.create ?? 'Create' }}
          </button>
        </div>
      </Teleport>
    </div>
</template>
